/* eslint-disable no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import moment from "moment";
import { useFormik } from "formik";
import * as yup from "yup";

import Input from "components/input";

/* MATERIAL TAILWIND COMPONENTS */
import { Button, Dialog, DialogBody, Tooltip } from "@material-tailwind/react";

/* ICONS */
import { BiEdit } from "react-icons/bi";
import { FiTrash } from "react-icons/fi";
import { IoClose } from "react-icons/io5";

/* TOAST NOTIFICATION */
import { toast } from "react-toast";

/* API IMPORT */
import {
  addCategory,
  deleteCategory,
  editCategory,
  getCategoryBlogList,
} from "service/api";

const validationSchema = yup.object().shape({
  name: yup.string().required("Category name is required."),
});

const Categories = () => {
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [sizePerPage, setSizePerPage] = useState(10);
  const [totalDocs, setTotalDocs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState("");

  const [addDialog, setAddDialog] = useState(false);
  const [editDialog, setEditDialog] = useState(false);
  const [editId, setEditId] = useState(null);

  const [deleteId, setDeleteId] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);

  const getCategoryListFunction = () => {
    setLoading(true);
    const params = {
      page: page,
      sizePerPage: sizePerPage,
    };
    if (search) {
      params.search = search;
    }

    getCategoryBlogList(params)
      .then((res) => {
        setCategories(res?.data?.docs || []);
        setTotalDocs(res?.data?.totalDocs || 0);
      })
      .catch((e) => {
        console.log("Error: ", e);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleDelete = () => {
    deleteCategory(deleteId)
      .then((res) => {
        toast.success(res?.message);
      })
      .catch((e) => {
        toast.error(e?.response?.data?.message);
      })
      .finally(() => {
        setDeleteModal(!deleteModal);
        setDeleteId(null);
        getCategoryListFunction();
      });
  };

  const addFormik = useFormik({
    initialValues: {
      name: "",
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      setLoading(true);
      addCategory(values)
        .then((res) => {
          if (res?.status) {
            toast.success(res?.message);
            addFormik.resetForm();
            setAddDialog(false);
            getCategoryListFunction();
          } else {
            toast.error(res?.message);
          }
        })
        .catch((e) => {
          toast.error(e?.response?.data?.message || "Failed to add category");
        })
        .finally(() => {
          setLoading(false);
        });
    },
  });

  const editFormik = useFormik({
    initialValues: {
      name: "",
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      setLoading(true);
      editCategory(editId, values)
        .then((res) => {
          if (res?.status) {
            toast.success(res?.message);
            editFormik.resetForm();
            setEditDialog(false);
            setEditId(null);
            getCategoryListFunction();
          } else {
            toast.error(res?.message);
          }
        })
        .catch((e) => {
          toast.error(e?.response?.data?.message || "Failed to edit category");
        })
        .finally(() => {
          setLoading(false);
        });
    },
  });

  useEffect(() => {
    getCategoryListFunction();
  }, [page, sizePerPage, search]);

  const LoadingScreen = () => {
    return (
      <div className="w-full bg-[#F5F5F5] bg-opacity-50 rounded-md border border-authBorder py-5">
        <p className="text-sm font-ibm-semibold text-black text-center">
          Please wait!
        </p>
        <p className="text-sm font-ibm-semibold text-black text-center">
          We are fetching data
        </p>
      </div>
    );
  };

  const NoDataScreen = () => {
    return (
      <div className="w-full bg-[#F5F5F5] bg-opacity-50 rounded-md border border-authBorder py-5">
        <p className="text-sm font-ibm-semibold text-black text-center">
          There are no categories to display.
        </p>
      </div>
    );
  };

  return (
    <section className="px-0 py-2 xl:p-5 lg:p-5 min-h-screen xl:min-h-full lg:min-h-full">
      <Helmet>
        <title>Categories | Farmers Insurance</title>
      </Helmet>

      <div className="container-full-width">
        <div className="block xl:flex lg:flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-ibm-bold text-black tracking-tight">
              Categories
            </h1>
            <h2 className="text-base font-ibm-regular text-greyText">
              Manage all blog categories.
            </h2>
          </div>

          <div className="block xl:flex lg:flex items-center gap-2 mt-2 xl:mt-0 lg:mt-0">
            <div className="w-full xl:w-searchBar lg:w-searchBar">
              <Input
                type="text"
                placeholder="Search..."
                name="search"
                id="search"
                value={search}
                onChange={(e) => setSearch(e?.target?.value)}
              />
            </div>

            <Button
              onClick={() => {
                addFormik.resetForm();
                setAddDialog(true);
              }}
              className="w-full xl:w-auto lg:w-auto mt-2 xl:mt-0 lg:mt-0 bg-logoYellow rounded-md py-3 px-8 text-sm font-ibm-semibold shadow-none hover:shadow-none normal-case hover:bg-opacity-80 transition-all duration-300 ease-in-out"
            >
              Add Category
            </Button>
          </div>
        </div>

        <div className="mt-5">
          {loading ? (
            <LoadingScreen />
          ) : categories.length < 1 ? (
            <NoDataScreen />
          ) : (
            <div className="grid gap-4 grid-cols-1 lg:grid-cols-5 xl:grid-cols-5">
              {categories.map((category, index) => (
                <div
                  key={category?._id || index}
                  className="bg-white shadow rounded-lg border border-authBorder p-4"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h5 className="text-base font-ibm-bold text-black">
                        {category?.name}
                      </h5>
                      <p className="text-sm font-ibm-regular text-greyText mt-1">
                        {moment(category?.createdAt).format("MMM DD, YYYY")}
                      </p>
                    </div>
                    <div className="flex items-center gap-1">
                      <Tooltip
                        content="Edit"
                        className="text-sm font-ibm-semibold"
                      >
                        <Button
                          onClick={() => {
                            setEditId(category?._id);
                            editFormik.setValues({
                              name: category?.name || "",
                            });
                            setEditDialog(true);
                          }}
                          className="p-2 border border-greyBorder rounded-full shadow-none hover:shadow-none bg-transparent hover:bg-black hover:bg-opacity-5"
                        >
                          <BiEdit className="w-4 h-4 text-greyText" />
                        </Button>
                      </Tooltip>
                      <Tooltip
                        content="Delete"
                        className="text-sm font-ibm-semibold"
                      >
                        <Button
                          onClick={() => {
                            setDeleteId(category?._id);
                            setDeleteModal(!deleteModal);
                          }}
                          className="p-2 border border-greyBorder rounded-full shadow-none hover:shadow-none bg-transparent hover:bg-black hover:bg-opacity-5"
                        >
                          <FiTrash className="w-4 h-4 text-greyText" />
                        </Button>
                      </Tooltip>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ADD CATEGORY */}
      <Dialog
        open={addDialog}
        handler={() => setAddDialog(!addDialog)}
        size="xs"
        animate={{
          mount: { scale: 1, y: 0 },
          unmount: { scale: 0.9, y: -100 },
        }}
      >
        <DialogBody className="p-0">
          <form onSubmit={addFormik.handleSubmit}>
            <div className="border-b border-authBorder px-5 py-3 flex items-center justify-between">
              <h5 className="text-lg font-ibm-bold text-black tracking-tight">
                Add Category
              </h5>
              <Button
                className="p-2 rounded-full bg-white shadow-none hover:shadow-none hover:bg-black hover:bg-opacity-5"
                onClick={() => setAddDialog(!addDialog)}
              >
                <IoClose className="w-4 h-4 text-greyText" />
              </Button>
            </div>
            <div className="p-5 border-b border-authBorder">
              <div className="form-group">
                <label className="text-sm font-ibm-regular text-greyText">
                  Category Name <span className="text-red-500">*</span>
                </label>
                <div className="mt-1">
                  <Input
                    type="text"
                    name="name"
                    id="name"
                    value={addFormik.values.name}
                    onChange={addFormik.handleChange}
                    error={
                      addFormik.errors.name && addFormik.touched.name
                        ? true
                        : false
                    }
                    placeholder="Write category name"
                  />
                </div>
                {addFormik.errors.name && addFormik.touched.name && (
                  <div className="text-red-500 text-xs mt-1">
                    {addFormik.errors.name}
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center justify-end px-5 py-3">
              <Button
                type="submit"
                className="text-sm shadow-none hover:shadow-none bg-logoYellow hover:bg-opacity-80 px-5 py-3 rounded-md font-ibm-semibold text-white transition-all duration-300 ease-in-out normal-case"
              >
                {loading ? "Please wait.." : "Save category"}
              </Button>
            </div>
          </form>
        </DialogBody>
      </Dialog>

      {/* EDIT CATEGORY */}
      <Dialog
        open={editDialog}
        handler={() => setEditDialog(!editDialog)}
        size="xs"
        animate={{
          mount: { scale: 1, y: 0 },
          unmount: { scale: 0.9, y: -100 },
        }}
      >
        <DialogBody className="p-0">
          <form onSubmit={editFormik.handleSubmit}>
            <div className="border-b border-authBorder px-5 py-3 flex items-center justify-between">
              <h5 className="text-lg font-ibm-bold text-black tracking-tight">
                Edit Category
              </h5>
              <Button
                className="p-2 rounded-full bg-white shadow-none hover:shadow-none hover:bg-black hover:bg-opacity-5"
                onClick={() => setEditDialog(!editDialog)}
              >
                <IoClose className="w-4 h-4 text-greyText" />
              </Button>
            </div>
            <div className="p-5 border-b border-authBorder">
              <div className="form-group">
                <label className="text-sm font-ibm-regular text-greyText">
                  Category Name <span className="text-red-500">*</span>
                </label>
                <div className="mt-1">
                  <Input
                    type="text"
                    name="name"
                    id="edit-name"
                    value={editFormik.values.name}
                    onChange={editFormik.handleChange}
                    error={
                      editFormik.errors.name && editFormik.touched.name
                        ? true
                        : false
                    }
                    placeholder="Write category name"
                  />
                </div>
                {editFormik.errors.name && editFormik.touched.name && (
                  <div className="text-red-500 text-xs mt-1">
                    {editFormik.errors.name}
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center justify-end px-5 py-3">
              <Button
                type="submit"
                className="text-sm shadow-none hover:shadow-none bg-logoYellow hover:bg-opacity-80 px-5 py-3 rounded-md font-ibm-semibold text-white transition-all duration-300 ease-in-out normal-case"
              >
                {loading ? "Please wait.." : "Save"}
              </Button>
            </div>
          </form>
        </DialogBody>
      </Dialog>

      {/* DELETE CATEGORY */}
      <Dialog
        open={deleteModal}
        handler={() => setDeleteModal(!deleteModal)}
        size="xs"
        animate={{
          mount: { scale: 1, y: 0 },
          unmount: { scale: 0.9, y: -100 },
        }}
      >
        <DialogBody className="p-0">
          <div className="border-b border-authBorder px-5 py-3 flex items-center justify-between">
            <h5 className="text-lg font-ibm-bold text-black tracking-tight">
              Delete
            </h5>
            <Button
              className="p-2 rounded-full bg-white shadow-none hover:shadow-none hover:bg-black hover:bg-opacity-5"
              onClick={() => setDeleteModal(!deleteModal)}
            >
              <IoClose className="w-4 h-4 text-greyText" />
            </Button>
          </div>
          <div className="p-5 border-b border-authBorder">
            <p className="text-base font-ibm-regular text-black">
              Are you sure you want to delete this category?
            </p>
          </div>
          <div className="flex items-center justify-end px-5 py-3">
            <Button
              onClick={handleDelete}
              className="text-sm shadow-none hover:shadow-none bg-logoYellow hover:bg-opacity-80 px-5 py-3 rounded-md font-ibm-semibold text-white transition-all duration-300 ease-in-out normal-case"
            >
              {loading ? "Please wait.." : "Confirm, delete"}
            </Button>
          </div>
        </DialogBody>
      </Dialog>
    </section>
  );
};

export default Categories;
